import React from "react";
import { Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import LanguageDropdown from "../LanguageDropdown/LanguageDropdown";
import { useReactiveVar } from "@apollo/client";
import { DeepPartial } from "@apollo/client/utilities";
import { currentBranchIdVar, currentUserEmailVar } from "../../../main/globalVar/state";
import { UserProxy, EmployeeProxy } from "@me-team/host/main/graphql/types";
import { cloneNameToDisplay } from "@me-team/common/src/components/Me-menu/utils/cloneNameUtils";

export type NavbarUserData = DeepPartial<
    Pick<UserProxy, "employee"> & {
        employee: Pick<EmployeeProxy, "image" | "name" | "surname" | "userBranchRelations">;
    }
>;

interface SidebarLoggedUserProps {
    userData?: NavbarUserData | null;
    setShowCompaniesModal?: (value: boolean) => void;
    setShowChangePasswordModal?: (value: boolean) => void;
}

const SidebarLoggedUser: React.FC<SidebarLoggedUserProps> = ({
    userData,
    setShowCompaniesModal,
    setShowChangePasswordModal,
}) => {
    const { t } = useTranslation();
    const basePath = process.env.REACT_APP_POS_URL;
    const currentUserEmail = useReactiveVar(currentUserEmailVar);
    const currentBranchId = useReactiveVar(currentBranchIdVar);
    const getRoleForCurrentBranch = (
        employee: DeepPartial<EmployeeProxy> | null | undefined,
        branchId: number | string | null
    ) => {
        const relation = employee?.userBranchRelations?.find((rel) => rel?.branch?.id === branchId);
        return cloneNameToDisplay(relation?.role?.name, t) || null;
    };

    const loggedEmployee = userData?.employee;
    const loggedEmployeeBranchRoleName = getRoleForCurrentBranch(loggedEmployee, currentBranchId);

    return (
        <div className="bg-violet rounded-3 p-3 mb-4">
            <div className="d-flex justify-content-start">
                <div
                    className="d-flex justify-content-center align-items-center flex-shrink-0 rounded-circle bg-white"
                    style={{ width: "40px", height: "40px" }}
                >
                    {loggedEmployee?.image ? (
                        <img
                            className="rounded-circle"
                            width={40}
                            height={40}
                            src={`${basePath}/${loggedEmployee?.image}`}
                            alt={`${loggedEmployee?.name} ${loggedEmployee?.surname}`}
                        />
                    ) : (
                        <i className="bi bi-person fs-6 text-primary"></i>
                    )}
                </div>

                <Col className="ms-2 text-truncate">
                    <p className="fs-base text-truncate mb-0">
                        {loggedEmployee?.name} {loggedEmployee?.surname}
                    </p>
                    <p className="fs-7 text-truncate text-grey mb-0">{loggedEmployeeBranchRoleName}</p>
                    <p className="fs-7 text-truncate mb-0">{currentUserEmail}</p>
                </Col>
                <div className="align-self-center">
                    <LanguageDropdown
                        branchId={String(currentBranchId)}
                        setShowCompaniesModal={setShowCompaniesModal}
                        setShowChangePasswordModal={setShowChangePasswordModal}
                        isLoggedUser
                    />
                </div>
            </div>
        </div>
    );
};

export default SidebarLoggedUser;
